import {
  confirmCarouselUpload,
  createCarouselBatch,
  type AssetUploadMetadata,
  type CarouselTarget,
  type CreateCarouselBatchResponse,
} from '../../api';
import {
  describeError,
  isAbort,
  putFileToSignedUrl,
  type BatchUploadDeps,
  type BatchUploadResult,
} from './batchUpload';
import { MAX_CAROUSELS_PER_BATCH } from '../../constants/assetTypes';
import { framesWithoutSource, isCarouselUploadable, type DetectedCarousel } from './carouselDetect';

/**
 * The carousel analogue of `runOlaUpload`: one asset per carousel, a PUT per
 * frame, one confirm once every frame has landed.
 *
 * Progress is per carousel, as it is per banner: a half-uploaded carousel is
 * not a partial success anyone can use, and one failed frame fails its
 * carousel while leaving the others alone.
 */

/** Carousels at a time. */
const CAROUSEL_CONCURRENCY = 2;

/** Frames of one carousel at a time. Six at most, and each can be large. */
const FRAME_CONCURRENCY = 3;

/** Matching `batchUpload.ts`: the last tenth of the bar belongs to confirm. */
const PUT_PROGRESS_CEILING = 90;

export interface CarouselUploadItem {
  /** The carousel's name, which is also the asset's `filename_original`. */
  name: string;
  frameCount: number;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  /** 0–100 across the carousel's bytes, reaching 100 only after confirm. */
  progress: number;
  error?: string;
  duplicateOf?: string[];
}

export interface CarouselUploadDeps {
  createCarouselBatch: typeof createCarouselBatch;
  confirmCarouselUpload: typeof confirmCarouselUpload;
  putFile: BatchUploadDeps['putFile'];
}

const DEFAULT_DEPS: CarouselUploadDeps = {
  createCarouselBatch,
  confirmCarouselUpload,
  putFile: putFileToSignedUrl,
};

export interface RunCarouselUploadOptions {
  carousels: DetectedCarousel[];
  label?: string | null;
  /** Carousel-level only; never copy or source links, which are per frame. */
  metadata?: AssetUploadMetadata | null;
  signal?: AbortSignal;
  onChange: (items: CarouselUploadItem[]) => void;
  deps?: Partial<CarouselUploadDeps>;
}

/**
 * Whether *target* plans exactly *carousel*'s frames: every position once, each
 * with the filename of the frame at that position.
 */
export function frameTargetsMatch(carousel: DetectedCarousel, target: CarouselTarget): boolean {
  if (target.frames.length !== carousel.frames.length) return false;
  const seen = new Set<number>();
  for (const frameTarget of target.frames) {
    const frame = carousel.frames[frameTarget.position];
    if (!frame || seen.has(frameTarget.position)) return false;
    if (frameTarget.filename !== frame.file.name) return false;
    seen.add(frameTarget.position);
  }
  return true;
}

/** Why a carousel cannot be sent yet, or null. The modal gates on the same check. */
export function carouselBlocker(carousel: DetectedCarousel): string | null {
  if (!isCarouselUploadable(carousel)) return carousel.issues.join('; ');
  const unsourced = framesWithoutSource(carousel);
  if (unsourced.length === 0) return null;
  return unsourced.length === 1
    ? `Frame ${unsourced[0]} has no source image`
    : `Frames ${unsourced.join(', ')} have no source image`;
}

export async function runCarouselUpload({
  carousels,
  label,
  metadata,
  signal,
  onChange,
  deps: overrides,
}: RunCarouselUploadOptions): Promise<BatchUploadResult> {
  const deps = { ...DEFAULT_DEPS, ...overrides };

  const items: CarouselUploadItem[] = carousels.map((carousel) => ({
    name: carousel.name.trim(),
    frameCount: carousel.frames.length,
    status: 'pending',
    progress: 0,
  }));
  const publish = () => onChange(items.map((item) => ({ ...item })));

  if (carousels.length === 0) {
    return { batchId: null, uploaded: 0, failed: 0, cancelled: false };
  }

  // The backend refuses the whole batch past this, so nothing is sent.
  if (carousels.length > MAX_CAROUSELS_PER_BATCH) {
    for (const item of items) {
      item.status = 'failed';
      item.error = `One upload takes at most ${MAX_CAROUSELS_PER_BATCH} carousels`;
    }
    publish();
    return { batchId: null, uploaded: 0, failed: items.length, cancelled: false };
  }

  // All-or-nothing, as the backend is: one blocked carousel sends nothing,
  // and each card says whether it is the problem or a bystander.
  const blockers = carousels.map(carouselBlocker);
  if (blockers.some((blocker) => blocker !== null)) {
    blockers.forEach((blocker, index) => {
      items[index].status = 'failed';
      items[index].error = blocker ?? 'Another carousel in this batch is not ready';
    });
    publish();
    return { batchId: null, uploaded: 0, failed: items.length, cancelled: false };
  }

  publish();

  let batch: CreateCarouselBatchResponse;
  try {
    batch = await deps.createCarouselBatch(
      {
        carousels: carousels.map((carousel) => ({
          name: carousel.name.trim(),
          frames: carousel.frames.map((frame) => ({
            filename: frame.file.name,
            content_type: frame.contentType,
            size: frame.file.size,
            source_image_id: frame.sourceImageId,
            copy_elements: frame.copy
              .map((row) => ({ type: row.type, value: row.value.trim() }))
              .filter((row) => row.value !== ''),
          })),
        })),
        label: label?.trim() ? label.trim() : null,
        metadata: metadata ?? null,
      },
      signal,
    );
  } catch (error) {
    const cancelled = isAbort(error);
    const reason = cancelled ? 'Cancelled' : describeError(error, 'Could not start the upload');
    for (const item of items) {
      item.status = 'failed';
      item.error = reason;
    }
    publish();
    return { batchId: null, uploaded: 0, failed: items.length, cancelled };
  }

  // Index i is the target for carousels[i], checked by name and frame count
  // rather than assumed: a reorder would otherwise put one carousel's frames
  // into another's asset and raise nothing. Within a carousel, each frame is
  // sent to the target for its position, so the plan must name every position
  // once, each with that frame's filename: confirm checks only image type and
  // size, so a swap would go through with every image under another frame's
  // source and copy.
  const mispaired =
    batch.carousels.length !== carousels.length ||
    carousels.some(
      (carousel, index) =>
        batch.carousels[index].name !== carousel.name.trim() ||
        !frameTargetsMatch(carousel, batch.carousels[index]),
    );
  if (mispaired) {
    for (const item of items) {
      item.status = 'failed';
      item.error = 'Server returned a mismatched upload plan';
    }
    publish();
    return { batchId: batch.batch_id, uploaded: 0, failed: items.length, cancelled: false };
  }

  let cancelled = false;

  const uploadCarousel = async (index: number): Promise<void> => {
    const carousel = carousels[index];
    const target = batch.carousels[index];
    const item = items[index];

    item.status = 'uploading';
    publish();

    const totalBytes = carousel.frames.reduce((sum, frame) => sum + frame.file.size, 0);
    const doneBytes = new Map<number, number>();
    const reportProgress = () => {
      let sent = 0;
      for (const bytes of doneBytes.values()) sent += bytes;
      item.progress = Math.round((sent / Math.max(1, totalBytes)) * PUT_PROGRESS_CEILING);
      publish();
    };

    let cursor = 0;
    const worker = async () => {
      while (cursor < target.frames.length) {
        const position = cursor++;
        const frameTarget = target.frames[position];
        const file = carousel.frames[frameTarget.position].file;
        await deps.putFile(frameTarget.upload_url, file, frameTarget.content_type, {
          signal,
          onProgress: (percent) => {
            doneBytes.set(position, (file.size * percent) / PUT_PROGRESS_CEILING);
            reportProgress();
          },
        });
        doneBytes.set(position, file.size);
        reportProgress();
      }
    };

    try {
      // Settled, not raced: when one frame fails, the others still in flight
      // are waited for before the carousel is marked failed, so nothing from
      // this run reports progress after it has returned. The failing worker
      // also stops any further frame from starting.
      const results = await Promise.allSettled(
        Array.from({ length: Math.min(FRAME_CONCURRENCY, target.frames.length) }, () =>
          worker().catch((error: unknown) => {
            cursor = target.frames.length;
            throw error;
          }),
        ),
      );
      const failure = results.find((result) => result.status === 'rejected');
      if (failure?.status === 'rejected') throw failure.reason;

      // Only once every frame is up: the backend checks them together.
      const confirmed = await deps.confirmCarouselUpload(target.asset_id, signal);

      item.status = 'done';
      item.progress = 100;
      if (confirmed.duplicate_of.length > 0) item.duplicateOf = confirmed.duplicate_of;
      publish();
    } catch (error) {
      if (isAbort(error)) {
        cancelled = true;
        item.status = 'failed';
        item.error = 'Cancelled';
        publish();
        return;
      }
      // Not confirmed: the row stays `uploading`, invisible and reapable.
      cursor = target.frames.length;
      item.status = 'failed';
      item.error = describeError(error, 'Upload failed');
      publish();
    }
  };

  let carouselCursor = 0;
  const carouselWorker = async () => {
    while (carouselCursor < carousels.length) {
      const index = carouselCursor++;
      if (cancelled) {
        if (items[index].status === 'pending') {
          items[index].status = 'failed';
          items[index].error = 'Cancelled';
          publish();
        }
        continue;
      }
      await uploadCarousel(index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CAROUSEL_CONCURRENCY, carousels.length) }, () =>
      carouselWorker(),
    ),
  );

  const uploaded = items.filter((item) => item.status === 'done').length;
  return { batchId: batch.batch_id, uploaded, failed: items.length - uploaded, cancelled };
}
